import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  api,
  ModelEntity,
  ModelProvider,
  ModelTier,
  ProviderInfo,
  ModelListResponse,
} from '../api';

/**
 * Options for the useModels hook
 */
export interface UseModelsOptions {
  /** Filter by provider */
  provider?: ModelProvider;
  /** Filter by tier */
  tier?: ModelTier;
  /** Auto-fetch on mount (default: true) */
  autoFetch?: boolean;
}

/**
 * Result returned by the useModels hook
 */
export interface UseModelsResult {
  /** All available models */
  models: ModelEntity[];
  /** Provider information with grouped models */
  providers: ProviderInfo[];
  /** Models grouped by provider key */
  modelsByProvider: Record<ModelProvider, ModelEntity[]>;
  /** Models grouped by tier */
  modelsByTier: Record<ModelTier, ModelEntity[]>;
  /** Get the recommended model for a provider */
  getRecommendedModel: (provider: ModelProvider) => ModelEntity | undefined;
  /** Get model by entity ID */
  getModelById: (entityId: string) => ModelEntity | undefined;
  /** Loading state */
  loading: boolean;
  /** Error if fetch failed */
  error: Error | null;
  /** Manually refresh models */
  refresh: () => Promise<void>;
}

/**
 * React hook for fetching and managing available AI model entities.
 *
 * Provides models grouped by provider and tier, with helper functions
 * to find recommended models and look up models by ID.
 *
 * @example
 * ```tsx
 * function ModelSelector() {
 *   const { models, providers, getRecommendedModel, loading } = useModels();
 *
 *   if (loading) return <Spinner />;
 *
 *   return (
 *     <Select>
 *       {providers.map(p => (
 *         <optgroup key={p.key} label={p.displayName}>
 *           {models
 *             .filter(m => m.provider === p.key)
 *             .map(m => (
 *               <option key={m.entityId} value={m.entityId}>
 *                 {m.name} {m.isRecommended && '(Recommended)'}
 *               </option>
 *             ))}
 *         </optgroup>
 *       ))}
 *     </Select>
 *   );
 * }
 * ```
 */
export function useModels(options: UseModelsOptions = {}): UseModelsResult {
  const { provider, tier, autoFetch = true } = options;

  const [data, setData] = useState<ModelListResponse | null>(null);
  const [loading, setLoading] = useState(autoFetch);
  const [error, setError] = useState<Error | null>(null);

  const fetchModels = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.listModels({ provider, tier });
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, [provider, tier]);

  // Auto-fetch on mount if enabled
  useEffect(() => {
    if (autoFetch) {
      fetchModels();
    }
  }, [autoFetch, fetchModels]);

  // Memoized derived data
  const models = useMemo(() => data?.models || [], [data]);
  const providers = useMemo(() => data?.providers || [], [data]);

  const modelsByProvider = useMemo(() => {
    const grouped: Record<ModelProvider, ModelEntity[]> = {
      openai: [],
      anthropic: [],
      google: [],
      xai: [],
    };

    for (const model of models) {
      if (!grouped[model.provider]) {
        grouped[model.provider] = [];
      }
      grouped[model.provider].push(model);
    }

    return grouped;
  }, [models]);

  const modelsByTier = useMemo(() => {
    const grouped: Record<ModelTier, ModelEntity[]> = {
      fast: [],
      balanced: [],
      powerful: [],
    };

    for (const model of models) {
      if (grouped[model.tier]) {
        grouped[model.tier].push(model);
      }
    }

    return grouped;
  }, [models]);

  const getRecommendedModel = useCallback(
    (providerKey: ModelProvider): ModelEntity | undefined => {
      const providerInfo = providers.find((p) => p.key === providerKey);
      if (!providerInfo?.recommendedModelId) return undefined;

      return models.find((m) => m.entityId === providerInfo.recommendedModelId);
    },
    [models, providers],
  );

  const getModelById = useCallback(
    (entityId: string): ModelEntity | undefined => {
      return models.find((m) => m.entityId === entityId);
    },
    [models],
  );

  return {
    models,
    providers,
    modelsByProvider,
    modelsByTier,
    getRecommendedModel,
    getModelById,
    loading,
    error,
    refresh: fetchModels,
  };
}
